import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { buildOfficeAttachmentQueryVariables } from '@confluence/office-macro-utils/entry-points/macroParamsUtils.ts';

import type {
	useViewXlsDataQuery$data,
	useViewXlsDataQuery$variables,
} from './__generated__/useViewXlsDataQuery.graphql.ts';

type ContentEdge = NonNullable<NonNullable<useViewXlsDataQuery$data['content']>['edges']>[number];
type AttachmentNode = NonNullable<
	NonNullable<NonNullable<NonNullable<ContentEdge>['node']>['attachments']>['nodes']
>[number];
type AttachmentExtension = NonNullable<NonNullable<AttachmentNode>['extensions']>[number];

export type ViewXlsDataResult = {
	mediaFileId: string | null;
	collectionName: string | null;
	sourceContentId: string | null;
	isLoading: boolean;
	error: Error | null;
};

export type UseViewXlsDataOptions = {
	contentId: string | null;
	attachmentName: string | null;
	pageName?: string | null;
	spaceKey?: string | null;
	date?: string | null;
};

/**
 * Hook that fetches the media file ID and collection name for an Office file
 * attachment via the AGG GraphQL content → attachments API.
 */
export const useViewXlsData = ({
	contentId,
	attachmentName,
	pageName,
	spaceKey,
	date,
}: UseViewXlsDataOptions): ViewXlsDataResult => {
	const normalizedPageName = pageName?.trim() || null;
	const isCrossPage = Boolean(normalizedPageName);
	const skip = !contentId || !attachmentName;

	const variables = buildOfficeAttachmentQueryVariables({
		contentId,
		pageName: normalizedPageName,
		spaceKey,
		date,
	});

	const { data, loading, error } = useQueryInterop<
		useViewXlsDataQuery$data,
		useViewXlsDataQuery$variables
	>(
		graphql`
			query useViewXlsDataQuery(
				$contentId: ID
				$contentStatus: [String]
				$spaceKey: String
				$title: String
				$type: String
				$postingDay: String
			) {
				content(
					id: $contentId
					status: $contentStatus
					spaceKey: $spaceKey
					title: $title
					type: $type
					postingDay: $postingDay
				) {
					edges {
						node {
							id
							attachments(first: 250) {
								nodes {
									title
									extensions {
										key
										value
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			variables,
			skip,
			fetchPolicy: 'cache-first',
		},
	);

	if (skip) {
		return {
			mediaFileId: null,
			collectionName: null,
			sourceContentId: null,
			isLoading: false,
			error: null,
		};
	}

	if (error) {
		return {
			mediaFileId: null,
			collectionName: null,
			sourceContentId: null,
			isLoading: false,
			error,
		};
	}

	const contentNode = data?.content?.edges?.[0]?.node;
	const attachmentNodes = contentNode?.attachments?.nodes ?? [];
	const matchingAttachment = attachmentNodes.find(
		(node: AttachmentNode) => node?.title?.toLowerCase() === attachmentName?.toLowerCase(),
	);

	const mediaFileId =
		matchingAttachment?.extensions?.find((ext: AttachmentExtension) => ext?.key === 'fileId')
			?.value ?? null;

	const resolvedContentId = isCrossPage ? (contentNode?.id ?? null) : contentId;
	const collectionName = resolvedContentId ? `contentId-${resolvedContentId}` : null;

	return {
		mediaFileId,
		collectionName,
		sourceContentId: resolvedContentId,
		isLoading: loading && !data,
		error: null,
	};
};
